import { disableFragmentWarnings, gql } from '@apollo/client';
import { DocumentNode, FragmentDefinitionNode, Location } from 'graphql';

/**
 * This module exports any graphQL queries, mutations, fragments, as well as functions that return them, as long as they
 * are relevant to long-running queries.
 */

export type EventQueryFragment = {
  definitions: Array<FragmentDefinitionNode>;
  loc: Location;
};

// Because we dynamically generate fragments for useLRQ with the name QueriesResult, disable the
// warning about duplicate fragments. The warning is to prevent us from declaring two fragments
// with the same name, then importing them, and passing the fragment name into a query. In our
// case we are passing in the correct fragment in as an argument to generateLaunchQuery and
// generatePingQuery functions, so we know we are dealing with the correct fragment.
disableFragmentWarnings();

/**
 * DocumentNode Generator Functions
 */

export function generateLaunchQuery(queriesResult: DocumentNode): DocumentNode {
  return gql`
    query launchQuery($queryGroupRequest: QueryGroupRequestInput!) {
      launchQuery(queryGroupRequest: $queryGroupRequest) {
        ...QueriesResult
      }
    }
    ${queriesResult}
  `;
}

export function generateLaunchDashboardQuery(queriesResult: DocumentNode): DocumentNode {
  return gql`
    query launchDashboardQuery($dashboardQuery: DashboardQueryGroupInput!) {
      launchDashboardQuery(dashboardQuery: $dashboardQuery) {
        ...QueriesResult
      }
    }
    ${queriesResult}
  `;
}

export function generatePingQuery(queriesResult: DocumentNode): DocumentNode {
  return gql`
    query pingQuery($ids: [String], $lastStepSeen: Int!, $token: String!) {
      pingQuery(ids: $ids, lastStepSeen: $lastStepSeen, token: $token) {
        ...QueriesResult
      }
    }
    ${queriesResult}
  `;
}

export function generateQueriesResultNode(fragmentDocuments: Array<EventQueryFragment>): DocumentNode {
  return gql`
    fragment QueriesResult on QueriesResult {
      ids
      status
      token
      stepsCompleted
      totalSteps
      results {
        id
        stepsCompleted
        totalSteps
        error
        data {
          matchCount
          ${fragmentDocuments.reduce(
    (accumulator, fragment) => accumulator + `...${fragment.definitions[0].name.value}\n`,
    '',
  )}
        }
      }
    }
     ${fragmentDocuments.reduce((accumulator, fragment) => accumulator + fragment.loc.source.body + '\n', '')}
  `;
}

export function generateAllDataNode(): DocumentNode {
  return gql`
    fragment AllQueryRequestResult on QueryRequestResult {
      id
      stepsCompleted
      totalSteps
      error
      data {
        matchCount
        outcome
        ... on FacetData {
          facets {
            name
            isNumeric
            matchCount
            sampledMatchCount
            uniqueValuesCount
            values {
              count
              value
            }
          }
          matchCount
          outcome
          sampledEventCount
        }
        ... on LogEntryData {
          matchCount
          matches {
            continuationToken
            logEntry {
              facetsNumberLimitExceeded
              facetsWithTruncatedValues
              parentTimestamps
              serverInfo
              sessionId
              severity
              threadId
              timestamp
              type
              values
            }
          }
          outcome
        }
        ... on PlotData {
          xAxis
          matchCount
          outcome
          plots {
            samples
            total
            label
            fn {
              param
              operator
            }
          }
          sliceInfo {
            defaultSize
            firstSize
            lastSize
            numSlices
          }
        }
        ... on PqResultData {
          columns {
            name
            format
            decimalPlaces
            type
          }
          values
          cells {
            value
            url
          }
          matchCount
          omittedEvents
          outcome
        }
        ... on HistogramData {
          matchCount
          outcome
          positiveXAxis
          positiveSampleCounts
          negativeXAxis
          negativeSampleCounts
          zeroCount
          p10
          p90
          p99
          p999
          max
          min
          mean
          median
        }
      }
    }
  `;
}

export function generateLoglineNode(): DocumentNode {
  return gql`
    fragment LoglineData on LogEntryEdge {
      continuationToken
      logEntry {
        facetsNumberLimitExceeded
        facetsWithTruncatedValues
        parentTimestamps
        serverInfo
        sessionId
        severity
        threadId
        timestamp
        type
        values
      }
    }
  `;
}

/**
 * EventQuery Result Fragments
 */

export const GRAPH_RESULT_FRAGMENT = (gql`
  fragment GraphResult on ResultData {
    ... on PlotData {
      xAxis
      matchCount
      outcome
      plots {
        samples
        total
        label
        fn {
          param
          operator
        }
      }
      sliceInfo {
        defaultSize
        firstSize
        lastSize
        numSlices
      }
    }
  }
` as unknown) as EventQueryFragment;

export const LOG_ENTRY_FRAGMENT = (gql`
  fragment LogEntryResult on ResultData {
    ... on LogEntryData {
      matchCount
      matches {
        continuationToken
        logEntry {
          facetsNumberLimitExceeded
          facetsWithTruncatedValues
          parentTimestamps
          serverInfo
          sessionId
          severity
          threadId
          timestamp
          type
          values
        }
      }
      outcome
    }
  }
` as unknown) as EventQueryFragment;

export const HISTOGRAM_GRAPH_RESULT_FRAGMENT = (gql`
  fragment GraphResult on ResultData {
    ... on PlotData {
      xAxis
      matchCount
      outcome
      plots {
        label
        samples
        total
      }
    }
    ... on FacetData {
      facets {
        name
        isNumeric
        matchCount
        sampledMatchCount
        uniqueValuesCount
        values {
          count
          value
        }
      }
      outcome
    }
    ... on HistogramData {
      matchCount
      outcome
      positiveXAxis
      positiveSampleCounts
      negativeXAxis
      negativeSampleCounts
      zeroCount
      p10
      p90
      p99
      p999
      max
      min
      mean
      median
    }
  }
` as unknown) as EventQueryFragment;

export const TOP_FACETS_RESULT_FRAGMENT = (gql`
  fragment TopFacetsResult on ResultData {
    ... on FacetData {
      facets {
        name
        isNumeric
        matchCount
        sampledMatchCount
        uniqueValuesCount
        values {
          count
          value
        }
      }
      matchCount
      outcome
      sampledEventCount
    }
  }
` as unknown) as EventQueryFragment;

export const POWER_QUERY_FRAGMENT = (gql`
  fragment PowerQueryResult on ResultData {
    ... on PqResultData {
      columns {
        name
        format
        decimalPlaces
        type
      }
      cells {
        value
        url
      }
      values
      matchCount
      omittedEvents
      outcome
      partialResultsDueToTimeLimit
    }
  }
` as unknown) as EventQueryFragment;
